import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { formatDateRange } from "@/lib/format";
import { Users, Flame, CalendarDays, ArrowRight } from "lucide-react";

export default function Organizers() {
  const { lang, t } = useLang();
  const { data: organizers = [] } = useQuery({ queryKey: ["organizersAll"], queryFn: async () => (await api.get("/organizers")).data });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "ItemList", name: t("organizers_title"),
    itemListElement: organizers.slice(0, 100).map((o, i) => ({
      "@type": "ListItem", position: i + 1, name: o.name, url: `${origin}/u/${o.user_id}`,
    })),
  };

  return (
    <>
      <Seo title={t("organizers_title")} description={t("organizers_desc")} canonicalPath="/organisateurs" jsonLd={jsonLd} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-3"><Users className="w-7 h-7 text-amber-500" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("organizers_title")}</h1></div>
        <p className="text-stone-400 mb-10 max-w-2xl">{t("organizers_desc")}</p>

        {organizers.length === 0 ? (
          <div className="text-center py-16 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="organizers-empty">{t("organizers_none")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="organizers-grid">
            {organizers.map((o) => (
              <Link key={o.user_id} to={`/u/${o.user_id}`} data-testid={`organizer-card-${o.user_id}`}
                className="group flex flex-col bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center gap-4 mb-4">
                  {o.picture ? <img src={o.picture} alt={o.name} className="w-14 h-14 rounded-full object-cover border-2 border-amber-800/40" /> : <span className="w-14 h-14 rounded-full bg-amber-900/40 flex items-center justify-center"><Flame className="w-6 h-6 text-amber-400" /></span>}
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-100 group-hover:text-amber-100 leading-tight line-clamp-1">{o.name}</div>
                    <div className="font-mono text-[11px] text-stone-500 mt-1">{o.follower_count} {t("followers")} · {o.event_count} {t("events_count")}</div>
                  </div>
                </div>
                {o.bio && <p className="text-sm text-stone-400 line-clamp-2 mb-4">{o.bio}</p>}
                {o.next_event ? (
                  <div className="mt-auto pt-4 border-t border-[#272A34]">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-amber-500/80 mb-1 flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> {t("organizers_next")}</div>
                    <div className="text-sm text-stone-200 line-clamp-1">{o.next_event.title}</div>
                    <div className="text-xs text-stone-500">{formatDateRange(o.next_event.start_date, o.next_event.start_date, lang)} · {o.next_event.city}</div>
                  </div>
                ) : (
                  <div className="mt-auto pt-4 border-t border-[#272A34] text-xs text-stone-500 inline-flex items-center gap-1.5">{t("organizers_view")} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
